import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MessageReaction } from '@anidex/shared/economy';
import type { ConversationDTO, MessageDTO, NotificationDTO, Page, SocialSummaryDTO } from '@anidex/shared/types';
import { api, queryString } from '../api';
import { invalidateKeys } from './cache';
import { keys } from './keys';
import { cursorPagination } from './pagination';

export function useSocialSummary(enabled = true) {
  return useQuery({
    queryKey: keys.summary,
    queryFn: () => api<SocialSummaryDTO>('/notifications/summary'),
    enabled,
    staleTime: 10_000,
  });
}

export function useNotifications() {
  return useInfiniteQuery({
    queryKey: keys.notifications,
    queryFn: ({ pageParam }) => api<Page<NotificationDTO>>(`/notifications${queryString({ cursor: pageParam })}`),
    ...cursorPagination,
  });
}

export function useConversations() {
  return useQuery({
    queryKey: keys.conversations,
    queryFn: () => api<{ items: ConversationDTO[] }>('/conversations'),
  });
}

export function useMessages(conversationId: string | null) {
  return useInfiniteQuery({
    queryKey: keys.messages(conversationId ?? ''),
    queryFn: ({ pageParam }) =>
      api<Page<MessageDTO>>(`/conversations/${conversationId}/messages${queryString({ cursor: pageParam })}`),
    ...cursorPagination,
    enabled: Boolean(conversationId),
  });
}

export function useStartConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<{ conversationId: string }>('/conversations', { body: { userId } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.conversations }),
  });
}

export function useSendMessage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { conversationId: string; content?: string; sharedCharacterId?: number; clientId: string }) =>
      api<{ message: MessageDTO }>('/messages', { body: input }),
    onSuccess: (_result, input) => {
      invalidateKeys(client, [keys.messages(input.conversationId), keys.conversations, keys.summary]);
    },
  });
}

export function useMarkConversationRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api<{ ok: boolean }>(`/conversations/${conversationId}/read`, { body: {} }),
    onSuccess: (_result, conversationId) => {
      invalidateKeys(client, [keys.conversations, keys.messages(conversationId), keys.summary, keys.notifications]);
    },
  });
}

export function useMuteConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, muted }: { conversationId: string; muted: boolean }) =>
      api<{ muted: boolean }>(`/conversations/${conversationId}`, { method: 'PATCH', body: { muted } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.conversations }),
  });
}

export function useDeleteConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api<{ ok: boolean }>(`/conversations/${conversationId}`, { method: 'DELETE' }),
    onSuccess: (_result, conversationId) => {
      invalidateKeys(client, [keys.conversations, keys.messages(conversationId), keys.summary]);
    },
  });
}

export function useToggleReaction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      reaction,
      active,
      conversationId,
    }: {
      messageId: string;
      reaction: MessageReaction;
      active: boolean;
      conversationId: string;
    }) =>
      active
        ? api<{ ok: boolean }>(`/messages/${messageId}/reactions/${reaction}`, { method: 'DELETE' })
        : api<{ ok: boolean }>(`/messages/${messageId}/reactions/${reaction}`, { method: 'PUT', body: {} }),
    onSuccess: (_result, { conversationId }) => {
      void client.invalidateQueries({ queryKey: keys.messages(conversationId) });
    },
  });
}

export function useDeleteMessage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, conversationId }: { messageId: string; conversationId: string }) =>
      api<{ ok: boolean }>(`/messages/${messageId}`, { method: 'DELETE' }),
    onSuccess: (_result, { conversationId }) => {
      invalidateKeys(client, [keys.messages(conversationId), keys.conversations]);
    },
  });
}

export function useMarkNotificationsRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input?: string[] | { ids?: string[]; path?: string }) => {
      const body = Array.isArray(input) ? { ids: input } : (input ?? {});
      return api<{ ok: boolean; summary: SocialSummaryDTO }>('/notifications/read', { body });
    },
    onSuccess: (result) => {
      client.setQueryData(keys.summary, result.summary);
      void client.invalidateQueries({ queryKey: keys.notifications });
    },
  });
}

export function useDeleteNotification() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/notifications/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidateKeys(client, [keys.notifications, keys.summary]);
    },
  });
}
